import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import api from '@/api'

export interface User {
  id: string
  email: string
  name?: string
  image?: string
  role?: string
}

export const useAuthStore = defineStore('email-auth', () => {
  const queryClient = useQueryClient()
  
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  
  const isAuthenticated = computed(() => !!user.value)
  
  // Cache auth check result to avoid repeated requests on navigation
  let lastAuthCheck = 0
  let authCheckPromise: Promise<void> | null = null
  
  async function initialize() {
    await fetchUser()
  }
  
  async function fetchUser() {
    // If we checked auth in the last 5 seconds and have a result, skip
    const now = Date.now()
    if (now - lastAuthCheck < 5000 && user.value !== undefined) {
      return
    }
    // If an auth check is already in flight, wait for it
    if (authCheckPromise) {
      return authCheckPromise
    }
    
    authCheckPromise = doFetchUser()
    try {
      await authCheckPromise
    } finally {
      lastAuthCheck = Date.now()
      authCheckPromise = null
    }
  }
  
  async function doFetchUser() {
    try {
      isLoading.value = true
      // Use Better-Auth session endpoint (cross-subdomain cookie auth)
      const response = await api.get('/auth/get-session')
      const sessionData = response.data
      if (sessionData?.user) {
        user.value = {
          id: sessionData.user.id,
          email: sessionData.user.email,
          name: sessionData.user.name || sessionData.user.email,
          image: sessionData.user.image,
          role: sessionData.user.role,
        }
      } else {
        user.value = null
      }
    } catch (err: any) {
      user.value = null
    } finally {
      isLoading.value = false
    }
  }
  
  function setUser(userData: User) {
    user.value = userData
  }
  
  async function logout() {
    try {
      // Call Better-Auth sign-out to invalidate server session
      await api.post('/auth/sign-out')
    } catch (err) {
      // Ignore errors - still clear local state
    }
    user.value = null
    localStorage.removeItem('token')
    delete api.defaults.headers.common['Authorization']
    queryClient.clear()
  }
  
  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    initialize,
    fetchUser,
    setUser,
    logout,
  }
})
